import styled from '@emotion/styled';
import { Typography, useMediaQuery } from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';

import ProductPricing from '../../../../entity/ProductPricing';
import { CreationSet, CreativeFormat, Option } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { calculateOptionMerchandisePoint } from '../../../../utility/calculatePoint';
import { SPACING_UNIT } from '../../../constant/spacing';
import { getResizeSortedCreativeFormats } from '../../../page/ProjectEditingPage/domain/helperFunctions';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import { InputtedCreationSet } from '../../../types/InputtedCreationSet';
import CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import CreativeFormatNetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';
import Spinner from '../Spinner';
import FormatByAttribute from './FormatByAttribute';

type Props = {
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  creationSet: InputtedCreationSet | CreationSet | undefined;
  pricingMerchandiseOption: Option;
};

const OptionPriceDetails: FC<Props> = ({ isPointRequired, productPricing, creationSet, pricingMerchandiseOption }) => {
  const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['lg']}px)`);
  return (
    <div>
      {creationSet &&
        getResizeSortedCreativeFormats((creationSet.creativeFormats as unknown) as ReadonlyArray<CreativeFormat>).map(
          (creativeFormat: CreativeFormat) => {
            const PriceDetailWithFormat = creativeFormat.resizeBaseId ? PriceDetail : PriceDetailBgGray;
            const formatOptionPoint = creationSet
              ? calculateOptionMerchandisePoint(creativeFormat, creationSet.expressDueDays || 0, [
                  pricingMerchandiseOption,
                ])
              : 0;
            return (
              <PriceDetailWithFormat key={`price-detail-with-format-${creativeFormat.id}`}>
                {/* CreativeType/Network/Placement表示 */}
                {isPC && <CreativeTypeIcon creativeType={creativeFormat.creativeType} size={24} />}
                <CreativeFormatNetworkIcon network={creativeFormat.network} size={24} />
                <PlacementName>
                  <SubTitle variant="subtitle1">
                    <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
                  </SubTitle>
                </PlacementName>
                {/* フォーマット表示 */}
                {creativeFormat.attribute && <FormatByAttribute creativeFormat={creativeFormat} />}
                {/* 価格表示 */}
                <Price>
                  {isPointRequired ? (
                    productPricing ? (
                      <>
                        {productPricing.currency}
                        {(formatOptionPoint * productPricing.flatRate).toLocaleString()}
                      </>
                    ) : (
                      <Spinner />
                    )
                  ) : null}
                </Price>
              </PriceDetailWithFormat>
            );
          },
        )}
    </div>
  );
};

const RequiredPoint = styled(Typography)`
  ${flexibleLayoutContainer({
    alignItems: 'center',
  })}
  font-weight: 700;
`;

RequiredPoint.defaultProps = { variant: 'body1', color: 'primary' };

const SubTitle = styled(Typography)`
  ${mq('sp')} {
    font-size: 0.8em;
  }
`;

SubTitle.defaultProps = { variant: 'subtitle1' };

const Price = styled('div')`
  font-weight: bold;
  margin-top: 3px;
  margin-left: auto;
  ${mq('sp')} {
    font-size: 0.8em;
  }
`;

const PriceDetail = styled('div')`
  ${flexibleLayoutContainer({
    alignItems: 'start',
    justifyContent: 'start',
  })}
  padding: ${SPACING_UNIT}px;
`;

const PriceDetailBgGray = styled('div')`
  ${flexibleLayoutContainer({
    alignItems: 'start',
    justifyContent: 'start',
  })}
  padding: ${SPACING_UNIT}px ${SPACING_UNIT}px ${SPACING_UNIT}px ${SPACING_UNIT}px;
  background-color: #f2f6fa;
`;

const CreativeTypeIcon = styled(CreativeFormatCreativeTypeIcon)`
  margin-right: ${SPACING_UNIT}px;
`;

const PlacementName = styled('div')`
  margin-left: ${SPACING_UNIT}px;
  width: 85px;
  word-break: break-word;
  ${mq('sp')} {
    width: 60px;
  }
`;

export default OptionPriceDetails;
